// config.js — Peerly application configuration
// All externalized constants, ICE server config, Firebase config, and tuning knobs.

/**
 * Firebase configuration.
 * Replace these values with your own Firebase project credentials.
 */
export const FIREBASE_CONFIG = {
  apiKey: 'AIzaSyD2BX3D7WXDb25_jZIWU_91OqbA8G_zUC0',
  authDomain: 'pyahlive.firebaseapp.com',
  projectId: 'pyahlive',
  storageBucket: 'pyahlive.firebasestorage.app',
  messagingSenderId: '718413273',
  appId: '1:718413273:web:27c74f735edc6ba5c10dc8',
  measurementId: 'G-KVML7G4Y96',
};

/**
 * ICE server configuration.
 *
 * STUN servers (default): Google public STUN.
 * TURN servers (optional): Add static entries here for testing, or use
 *   ICE_CONFIG_ENDPOINT below for dynamic credential fetching.
 *
 * TURN credentials can be:
 *   1. Hard-coded in TURN_SERVERS below (not recommended for production).
 *   2. Fetched at runtime from ICE_CONFIG_ENDPOINT (recommended).
 *   3. Injected via environment variable at build time.
 *
 * Without TURN, calls will fail when both peers are behind symmetric NATs or strict firewalls.
 * The app will report ICE failures honestly to the user.
 */
export const DEFAULT_ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

/**
 * Static TURN server entries (merged with dynamically fetched servers).
 * Uncomment and replace with your own TURN server credentials for testing.
 */
export const TURN_SERVERS = [
  // {
  //   urls: 'turn:your-turn-server.example.com:3478',
  //   username: 'username',
  //   credential: 'credential',
  // },
  // {
  //   urls: 'turn:your-turn-server.example.com:3478?transport=tcp',
  //   username: 'username',
  //   credential: 'credential',
  // },
];

/**
 * Optional: endpoint that returns ICE server config (including TURN credentials).
 * Set to null to skip dynamic loading and use DEFAULT_ICE_SERVERS only.
 * Expected response shape: { iceServers: [ { urls, username?, credential? } ] }
 */
export const ICE_CONFIG_ENDPOINT =
  'https://us-central1-peerly-57767.cloudfunctions.net/getIceServers'; // This works from any domain

/**
 * Loads ICE servers, merging defaults with any dynamically fetched TURN credentials.
 * Falls back to defaults on failure.
 */
export async function loadIceServers() {
  // Start with STUN defaults + static TURN servers
  const baseServers = [...DEFAULT_ICE_SERVERS, ...TURN_SERVERS];

  if (!ICE_CONFIG_ENDPOINT) {
    return { iceServers: baseServers };
  }
  try {
    const res = await fetch(ICE_CONFIG_ENDPOINT, { signal: AbortSignal.timeout(5000) });
    if (!res.ok) throw new Error(`ICE config fetch failed: ${res.status}`);
    const data = await res.json();
    if (Array.isArray(data.iceServers) && data.iceServers.length > 0) {
      return { iceServers: [...baseServers, ...data.iceServers] };
    }
  } catch (err) {
    console.warn('[config] Failed to load dynamic ICE servers, using defaults:', err);
  }
  return { iceServers: baseServers };
}

/**
 * Canonical base URL for the app. Used for OG tags, share links, etc.
 * Set to '' to use window.location.origin + pathname at runtime.
 */
export const BASE_URL = '';

/** Room / session tuning */
export const ROOM = {
  /** How long (ms) before a room is considered stale and can be overwritten without prompt */
  STALE_THRESHOLD_MS: 5 * 60 * 1000,
  /** Heartbeat interval for room presence (ms) */
  HEARTBEAT_INTERVAL_MS: 60_000,
  /** Timeout to batch ICE candidates before flushing to Firestore (ms) */
  ICE_CANDIDATE_BATCH_TIMEOUT_MS: 500,
  /** How many missed heartbeats before a participant is considered gone */
  HEARTBEAT_MISS_LIMIT: 3,
  /** Max room slug length */
  MAX_SLUG_LENGTH: 48,
  /** Allowed slug characters regex */
  SLUG_PATTERN: /^[a-z0-9][a-z0-9-]*[a-z0-9]$/,
  /** Reserved slugs that cannot be used as room names */
  RESERVED_SLUGS: new Set([
    'admin',
    'api',
    'app',
    'auth',
    'config',
    'help',
    'login',
    'logout',
    'null',
    'settings',
    'static',
    'status',
    'system',
    'test',
    'undefined',
    'zoot',
    'peerly',
    'www',
    'new',
    'join',
    'create',
    'room',
  ]),
};

/** Session ID generation word lists */
export const ANIMALS = [
  'cat',
  'dog',
  'fox',
  'bear',
  'lion',
  'seal',
  'crab',
  'fish',
  'bird',
  'duck',
  'wolf',
  'deer',
  'frog',
  'hawk',
  'lynx',
  'mole',
  'newt',
  'owl',
  'puma',
  'ram',
];

export const ADJECTIVES = [
  'bold',
  'calm',
  'cool',
  'cozy',
  'epic',
  'fair',
  'fast',
  'glad',
  'keen',
  'kind',
  'loud',
  'neat',
  'nice',
  'pure',
  'safe',
  'slim',
  'soft',
  'tidy',
  'warm',
  'wise',
];

/** Emoji list for reactions and picker */
export const EMOJI_LIST = [
  '😊',
  '❤️',
  '👍',
  '😂',
  '🎉',
  '🔥',
  '✨',
  '🎈',
  '🌟',
  '💖',
  '🚀',
  '🌈',
  '🤔',
  '😮',
  '😢',
  '🙏',
  '💯',
  '🙌',
  '😎',
  '🤗',
  '😍',
  '🥳',
  '🤩',
  '😇',
  '🤯',
  '👏',
  '💪',
  '🎯',
  '⭐',
  '🏆',
];

export const EMOJIS_PER_PAGE = 10;

/** Chat font size options */
export const FONT_SIZES = ['0.8rem', '1rem', '1.2rem'];
export const FONT_LABELS = ['S', 'M', 'L'];

/** DataChannel protocol constraints */
export const PROTOCOL = {
  MAX_CHAT_LENGTH: 2000,
  MAX_NICKNAME_LENGTH: 30,
  MAX_COMMAND_LENGTH: 500,
  MAX_STYLE_LENGTH: 20,
  MAX_FILE_NAME_LENGTH: 255,
  MAX_FILE_SIZE: 10 * 1024 * 1024 * 1024, // 10 GB
  FILE_CHUNK_SIZE: 16 * 1024, // 16 KB chunks
  ALLOWED_MESSAGE_TYPES: new Set([
    'chat',
    'command',
    'emoji',
    'nickname',
    'pyah',
    'celebration',
    'presence',
    'file-start',
    'file-chunk',
    'file-end',
    'file-accept',
    'file-deny',
  ]),
};

/** ICE restart / reconnection tuning */
export const ICE_RESTART = {
  /** Delay before triggering ICE restart after disconnection (ms) */
  RESTART_DELAY_MS: 5_000,
  /** Maximum restart attempts before giving up */
  MAX_ATTEMPTS: 6,
  /** Base interval between restart attempts if the first fails (ms) */
  RETRY_INTERVAL_MS: 3_000,
  /** Exponential backoff multiplier applied to retry interval */
  BACKOFF_MULTIPLIER: 1.5,
  /** Maximum backoff interval cap (ms) */
  MAX_BACKOFF_MS: 30_000,
};

/** Bandwidth adaptation tuning */
export const BITRATE = {
  /** Default max video bitrate (bps) — used as the starting ceiling */
  VIDEO_MAX: 2_500_000,
  /** Reduced bitrate when quality is 'weak' (bps) */
  VIDEO_REDUCED: 1_000_000,
  /** Further reduced bitrate when quality is 'poor' (bps) */
  VIDEO_MINIMAL: 400_000,
  /** Default max audio bitrate (bps) */
  AUDIO_MAX: 128_000,
  /** Reduced audio bitrate when quality is 'poor' (bps) */
  AUDIO_REDUCED: 64_000,
};

/** AI assistant config — OpenAI-compatible API (OpenRouter / custom endpoint) */
export const AI = {
  /** Default API endpoint (OpenRouter). User can override in settings. */
  DEFAULT_ENDPOINT: 'https://openrouter.ai/api/v1/chat/completions',
  /** Default model — user can override in settings. */
  DEFAULT_MODEL: 'openai/gpt-4o-mini',
  /** System prompt prepended to all /ask queries. */
  SYSTEM_PROMPT:
    'You are a helpful assistant inside a peer-to-peer video call app called Peerly. Keep answers concise and friendly. Use markdown sparingly.',
  /** Max tokens for AI responses. */
  MAX_TOKENS: 1024,
  /** Temperature for AI responses. */
  TEMPERATURE: 0.7,
  /** Request timeout in ms. */
  TIMEOUT_MS: 30_000,
  /** localStorage keys for persisted settings. */
  STORAGE_KEYS: {
    ENDPOINT: 'peerly.ai.endpoint',
    API_KEY: 'peerly.ai.apiKey',
    MODEL: 'peerly.ai.model',
  },
  /** Max length for /ask query text. */
  MAX_QUERY_LENGTH: 1000,
};

/** Analytics config — disabled by default for privacy-first posture */
export const ANALYTICS = {
  enabled: false,
  trackerUrl: 'https://astrilytics.vercel.app/tracker.js',
  apiUrl: 'https://astrilytics.vercel.app/api/track',
};
